'use client';

import HomePageTitle from '@/app/(home-page)/components/HomePageTitle';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import type { JumpTargetItem, NavigationListProps, TrackingParams } from './types';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';

/**
 * getAlt工具函数
 */
const getAlt = (componentName: string, index: number, indexData?: any): string => {
  try {
    if (!indexData?.jump_target) return `${componentName} ${index}`;

    const item = indexData.jump_target[index];
    if (!item) return `${componentName} ${index}`;

    // 优先级: title > 商品名称 > 分组名称 > 默认
    return (
      item.title || item.sku?.short_name || item.product_group?.name || `${componentName} ${index}`
    );
  } catch (error) {
    logger.error('getAlt error:', error);
    return `${componentName} ${index}`;
  }
};

/**
 * 埋点参数构建Hook
 */
const useTrackingParams = (
  item: JumpTargetItem,
  index: number,
  moduleIndex: number,
  componentId: string,
  moduleTitle: string,
  moduleId: string | number,
): TrackingParams => {
  return useMemo(
    () => ({
      module_index: moduleIndex,
      module_name: componentId,
      module_id: String(moduleId),
      mkt_index: index,
      mkt_index_sub: '',
      mkt_jump_type: `${item.jump_type}`,
      mkt_jump_content: `${item.Jump_target}`,
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: `${item.sku?.shop_sku || ''}`,
      business_sku: `${item.sku?.business_sku || ''}`,
      product_sku: `${item.sku?.sku || ''}`,
      product_id: `${item.sku?.id || ''}`,
      product_name: `${item.sku?.short_name || ''}`,
      group_id: `${item.product_group?.id || ''}`,
      group_name: `${item.product_group?.name || ''}`,
      module_title: moduleTitle,
    }),
    [item, index, moduleIndex, componentId, moduleTitle, moduleId],
  );
};

/**
 * 埋点逻辑Hook
 */
const useTrackingHandlers = (
  trackingParams: TrackingParams,
  onTrack?: (params: TrackingParams) => void,
  onExpTrack?: (params: TrackingParams) => void,
  onValidExpTrack?: (params: TrackingParams) => void,
) => {
  // 曝光埋点
  const [expRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 处理曝光埋点
  useEffect(() => {
    if (inView && onExpTrack) {
      try {
        onExpTrack(trackingParams);
      } catch (error) {
        logger.error('Navigation exposure tracking error:', error);
      }
    }
  }, [inView, onExpTrack, trackingParams]);

  // 处理点击埋点
  const handleClick = useCallback(() => {
    if (onTrack) {
      try {
        onTrack(trackingParams);
      } catch (error) {
        logger.error('Navigation click tracking error:', error);
      }
    }
  }, [onTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });

  return { expRef, handleClick, validExposureRef };
};

/**
 * 导航项组件 - 响应式统一实现
 */
const NavigationItem = ({
  item,
  index,
  onTrack,
  onExpTrack,
  moduleIndex,
  componentId,
  moduleTitle,
  indexData,
  onValidExpTrack,
}: {
  item: JumpTargetItem;
  index: number;
  onTrack?: (params: TrackingParams) => void;
  onExpTrack?: (params: TrackingParams) => void;
  moduleIndex: number;
  componentId: string;
  moduleTitle: string;
  indexData: any;
  onValidExpTrack?: (params: TrackingParams) => void;
}) => {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  // 获取埋点参数
  const trackingParams = useTrackingParams(
    item,
    index,
    moduleIndex,
    componentId,
    moduleTitle,
    indexData?.blocks_advances_id,
  );

  // 获取埋点处理函数
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  );
  const navigationItemRef = useMergedRefs(expRef, validExposureRef);
  // MagicJump props
  const jumpProps = useMemo(
    () => ({
      jump_type: item.jump_type,
      Jump_target: item.Jump_target,
      ...(item.sku?.shop_sku && { sku: { shop_sku: item.sku.shop_sku } }),
      ...(item.product_group?.id && {
        product_group: { id: item.product_group.id, path: item.product_group.path },
      }),
    }),
    [item],
  );

  // 样式配置 - 移动端优先，遵循原Vue组件设计
  const buttonStyles = useMemo(() => {
    const bgColor = item.text_bg_color || '#ffffff';
    const textColor = item.text_color || '#282828';
    const borderColor = item.text_color || '#282828';

    return {
      backgroundColor: bgColor,
      color: textColor,
      borderColor,
    };
  }, [item.text_bg_color, item.text_color]);

  return (
    <div
      ref={navigationItemRef}
      // 移动端：宽度60px，垂直布局 | PC端：flex项目，使用gap
      className={`
        ${isPC ? '' : 'w-[60px] flex flex-col items-center'}
      `}
    >
      <MagicJump
        prefetch
        item={jumpProps}
        onBeforeJump={handleClick}
        aria-label={item.title}
        className="cursor-pointer"
      >
        {isPC ? (
          // PC端：按钮样式 - 基于原NavigationListSmall.vue
          <div
            className="
              flex items-center justify-center
              h-[35px] px-[20px] lg:h-[40px] lg:px-[27px]
              rounded-[50px] border border-transparent font-br
              text-[14px] lg:text-[16px]
            "
            style={buttonStyles}
          >
            {item.title}
          </div>
        ) : (
          // 移动端：图标+文字布局 - 基于原CategoryNavSmall2025.vue
          <div className="flex flex-col items-center w-full">
            {item.jump_image && (
              <Image
                src={item.jump_image}
                alt={getAlt('CategoryNavSmall', index, indexData)}
                width={48}
                height={48}
                loading="eager"
                className="w-[48px] h-[48px] object-cover"
                style={{
                  aspectRatio: item.jump_img_ratio || 'auto',
                }}
              />
            )}
            <span
              className="
                w-full max-w-[60px] text-center text-[11px] leading-none mt-[5px]
                whitespace-nowrap overflow-hidden text-ellipsis
                block
              "
              style={{ color: item.text_color || '#282828' }}
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
          </div>
        )}
      </MagicJump>
    </div>
  );
};

/**
 * 小分类导航组件 - 基于原Vue组件的样式迁移
 */
export default function CategoryNavSmall({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: NavigationListProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;
  // 如果没有数据，不渲染
  if (!indexData?.jump_target?.length) {
    return null;
  }

  return (
    <div className={`${isPC ? 'mx-[24px] lg:mx-[48px]' : 'w-full'}`}>
      {/* 容器样式 - 移动端优先 */}
      <div
        className={`
          ${isPC ? 'my-[30px] lg:my-[50px] 2xl:my-[80px]' : 'my-[15px]'}
        `}
      >
        {/* 标题部分 - 仅PC端显示 */}
        <div className={`${isPC ? 'mb-[24px]' : ''}`}>
          <HomePageTitle
            indexData={indexData}
            variant="default"
            displayCondition="pc-only"
            titleTag="h1"
            subtitleTag="h5"
            hasButton={false}
          />
        </div>

        {/* 导航项列表容器 */}
        <div
          className={`
             ${
               isPC
                 ? 'flex flex-wrap items-center justify-center gap-y-[8px] gap-x-[8px] lg:gap-y-[12px] lg:gap-x-[12px] 2xl:gap-y-[16px] 2xl:gap-x-[16px]'
                 : 'flex items-center justify-center'
             }
           `}
        >
          {indexData.jump_target.map((item, index) => (
            <NavigationItem
              key={item.id || index}
              item={item}
              index={index}
              onTrack={onTrack}
              onExpTrack={onExpTrack}
              moduleIndex={moduleIndex}
              componentId={indexData.component_id}
              moduleTitle={indexData.layout_title}
              indexData={indexData}
              onValidExpTrack={onValidExpTrack}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
